import * as THREE from 'three';
import {
  DEMO_TARGET_LAYOUT,
  TARGETS,
  WARHEADS,
  WarheadKind,
  type TargetKindId,
  type TargetParams,
  type TargetPlacement,
  type WarheadKindId,
} from '../config/targets';
import type { Checksum, Checksummable } from '../core/Checksum';
import { bus } from '../core/EventBus';
import type { Mission } from '../mission/schema';
import type { Terrain } from '../world/Terrain';
import {
  ImpactGrade,
  createImpactResult,
  diveAngleDeg,
  resolveImpact,
  satisfiesObjective,
  type ImpactResult,
} from './Impact';

/**
 * Everything that can be attacked, and the collision that resolves an attack (SPEC §3.8).
 *
 * The aircraft *is* the weapon — this is a one-way drone — so an impact is the terminal event of
 * a mission rather than one of many. That is why the resolution is graded and why the geometry is
 * swept: a run that ends 3 m from the aim point at 140 m/s in a 45° dive has earned a different
 * outcome from one that clips the same target flat and slow, and both have to be distinguishable
 * on the single occasion the player gets.
 *
 * Collision is a swept sphere against the target's body radius, sampled along the tick's motion
 * segment for the same reason the terrain sweep is (SPEC §4.4): at 140 m/s a 120 Hz tick covers
 * 1.2 m, and a 2.5 m mast would otherwise be stepped straight through.
 */

export interface TargetInstance {
  readonly designation: string;
  readonly params: TargetParams;
  /** Base of the target, on the terrain. */
  readonly positionM: THREE.Vector3;
  /** The point an attack is graded against (SPEC §3.8.1). */
  readonly aimPointM: THREE.Vector3;
  readonly headingRad: number;
  /** 1 = intact, 0 = destroyed. */
  integrity: number;
  destroyed: boolean;
  /** Seconds of secondary fire remaining after destruction. */
  fireRemainingS: number;
  /** How the attack that destroyed it graded, for the debrief. */
  finalGrade: string;
  /** Human-readable name from the mission, shown on the HUD and the briefing map. */
  label: string;
}

/** Reused payload: emitted from inside the tick (SPEC §4.3). */
const impactEvent = {
  designation: '',
  kind: '' as TargetKindId,
  grade: '' as string,
  failure: '' as string,
  effect: 0,
  aimPointDistanceM: 0,
  impactSpeedMs: 0,
  diveAngleDeg: 0,
  destroyed: false,
  positionM: new THREE.Vector3(),
  secondaryFireS: 0,
};

const sweepPoint = new THREE.Vector3();
const segment = new THREE.Vector3();
const toTarget = new THREE.Vector3();

export class TargetField implements Checksummable {
  readonly targets: TargetInstance[] = [];

  /** The warhead fitted. Chosen at brief from M10; fixed until then. */
  warhead: WarheadKindId = WarheadKind.Fragmentation;

  private readonly terrain: Terrain;
  private readonly result: ImpactResult = createImpactResult();

  constructor(terrain: Terrain) {
    this.terrain = terrain;
  }

  /** Place a target. Missions call this at M8. */
  add(placement: TargetPlacement): void {
    const params = TARGETS[placement.kind];
    const groundY = this.terrain.surfaceHeightAt(placement.xM, placement.zM);
    const positionM = new THREE.Vector3(placement.xM, groundY, placement.zM);
    const aimPointM = positionM.clone();
    aimPointM.y += params.aimPointHeightM;

    this.targets.push({
      designation: placement.designation,
      params,
      positionM,
      aimPointM,
      headingRad: ((placement.headingDeg ?? 0) * Math.PI) / 180,
      integrity: 1,
      destroyed: false,
      fireRemainingS: 0,
      finalGrade: '',
      label: placement.designation,
    });
  }

  /** The placeholder laydown, used by Free Flight and the test harnesses. */
  loadDemoLayout(): void {
    for (let i = 0; i < DEMO_TARGET_LAYOUT.length; i += 1) this.add(DEMO_TARGET_LAYOUT[i]);
  }

  /**
   * Place a mission's targets, applying per-target overrides (SPEC §6.1).
   *
   * As with threats, overrides land on a copy: `TARGETS[kind]` is shared, and a mission that
   * softened one bunker would otherwise soften every bunker for the rest of the session.
   *
   * The instance's `designation` is the mission's target id, because that is the string objectives
   * refer to and the string `target:impact` carries — keeping them the same is what lets a
   * data-authored objective know that the thing it named is the thing that died.
   */
  loadMission(mission: Mission): void {
    this.warhead = mission.loadout.warhead;
    for (let i = 0; i < mission.targets.length; i += 1) {
      const entry = mission.targets[i];
      const base = TARGETS[entry.type];
      const params: TargetParams = {
        ...base,
        ...(entry.hardness === undefined ? {} : { hardness: entry.hardness }),
        ...(entry.requiredDiveAngleDeg === undefined
          ? {}
          : { minDiveAngleDeg: entry.requiredDiveAngleDeg }),
        ...(entry.requiredImpactSpeedMs === undefined
          ? {}
          : { minImpactSpeedMs: entry.requiredImpactSpeedMs }),
      };
      const groundY = this.terrain.surfaceHeightAt(entry.position[0], entry.position[2]);
      const positionM = new THREE.Vector3(entry.position[0], groundY, entry.position[2]);
      const aimPointM = positionM.clone();
      aimPointM.y += params.aimPointHeightM;

      this.targets.push({
        designation: entry.id,
        params,
        positionM,
        aimPointM,
        headingRad: ((entry.headingDeg ?? 0) * Math.PI) / 180,
        integrity: 1,
        destroyed: false,
        fireRemainingS: 0,
        finalGrade: '',
        label: entry.label,
      });
    }
  }

  /** Clear every placement, so a new mission starts from an empty theatre. */
  resetLayout(): void {
    this.targets.length = 0;
  }

  reset(): void {
    for (let i = 0; i < this.targets.length; i += 1) {
      const target = this.targets[i];
      target.integrity = 1;
      target.destroyed = false;
      target.fireRemainingS = 0;
      target.finalGrade = '';
    }
  }

  /**
   * Step 10–11 of the tick order (SPEC §4.3): collision against structures, then damage.
   *
   * Returns true when the aircraft struck something, which ends the run. The caller owns what
   * happens next — the impact cam, the debrief, the mission outcome.
   */
  update(
    dtS: number,
    previousPositionM: THREE.Vector3,
    positionM: THREE.Vector3,
    velocityMs: THREE.Vector3,
  ): boolean {
    this.burnSecondaries(dtS);

    const struck = this.sweepForImpact(previousPositionM, positionM, velocityMs);
    return struck;
  }

  /** Count down the sustained fires a destroyed target leaves behind (SPEC §5.3). */
  private burnSecondaries(dtS: number): void {
    for (let i = 0; i < this.targets.length; i += 1) {
      const target = this.targets[i];
      if (target.fireRemainingS > 0) target.fireRemainingS = Math.max(0, target.fireRemainingS - dtS);
    }
  }

  /**
   * Swept test along the tick's motion, resolving the closest target struck.
   *
   * The distance that matters is the **closest approach of the segment to the aim point**, not the
   * first point at which the aircraft entered the target's body. Those are very different numbers:
   * a bunker has a 9 m body, so an aircraft flying dead at the aim point crosses the body edge
   * 9 m out, and grading there would report a perfect run as a near miss and quietly make every
   * DIRECT HIT unreachable. Solved exactly rather than sampled — a point-to-segment distance is
   * cheaper than four samples and has no step size to be wrong about.
   */
  private sweepForImpact(
    previousPositionM: THREE.Vector3,
    positionM: THREE.Vector3,
    velocityMs: THREE.Vector3,
  ): boolean {
    segment.copy(positionM).sub(previousPositionM);
    const segmentLengthSq = segment.lengthSq();
    if (segmentLengthSq < 1e-12) return false;

    for (let i = 0; i < this.targets.length; i += 1) {
      const target = this.targets[i];
      if (target.destroyed) continue;

      toTarget.copy(target.aimPointM).sub(previousPositionM);
      const along = toTarget.dot(segment) / segmentLengthSq;

      // Trigger on entering the body: clamped to this tick's segment, so the strike happens on
      // the tick the aircraft actually arrives.
      const clamped = Math.min(1, Math.max(0, along));
      sweepPoint.copy(previousPositionM).addScaledVector(segment, clamped);
      if (sweepPoint.distanceTo(target.aimPointM) > target.params.bodyRadiusM) continue;

      // Grade on the *un*clamped closest approach — the perpendicular distance from the aim point
      // to the flight line. That is how close this run was ever going to get, which is the number
      // the player earned. Using the clamped distance instead would grade the moment the aircraft
      // crossed the body's edge, reporting a perfect run as 10 m off and making DIRECT HIT
      // unreachable on anything with a large body.
      sweepPoint.copy(previousPositionM).addScaledVector(segment, along);
      const distanceM = sweepPoint.distanceTo(target.aimPointM);

      this.resolve(target, distanceM, velocityMs, sweepPoint);
      return true;
    }
    return false;
  }

  private resolve(
    target: TargetInstance,
    aimPointDistanceM: number,
    velocityMs: THREE.Vector3,
    impactPointM: THREE.Vector3,
  ): void {
    const warhead = WARHEADS[this.warhead];
    const result = resolveImpact(
      aimPointDistanceM,
      velocityMs.length(),
      diveAngleDeg(velocityMs),
      target.params,
      warhead,
      this.result,
    );

    target.integrity = Math.max(0, target.integrity - result.effect);
    target.finalGrade = result.grade;

    const destroyed = satisfiesObjective(result.grade) || target.integrity <= 0;
    if (destroyed) {
      target.destroyed = true;
      target.integrity = 0;
      target.fireRemainingS = target.params.secondaryFireS;
    }

    impactEvent.designation = target.designation;
    impactEvent.kind = target.params.id;
    impactEvent.grade = result.grade;
    impactEvent.failure = result.failure;
    impactEvent.effect = result.effect;
    impactEvent.aimPointDistanceM = result.aimPointDistanceM;
    impactEvent.impactSpeedMs = result.impactSpeedMs;
    impactEvent.diveAngleDeg = result.diveAngleDeg;
    impactEvent.destroyed = destroyed;
    impactEvent.positionM.copy(impactPointM);
    impactEvent.secondaryFireS = destroyed ? target.params.secondaryFireS : 0;
    bus.emit('target:impact', impactEvent);
  }

  /** Targets currently burning, for the effects system. */
  get burning(): readonly TargetInstance[] {
    return this.targets;
  }

  /** How many objectives have been satisfied. Scoring proper arrives at M8. */
  get destroyedCount(): number {
    let count = 0;
    for (let i = 0; i < this.targets.length; i += 1) if (this.targets[i].destroyed) count += 1;
    return count;
  }

  checksum(into: Checksum): void {
    for (let i = 0; i < this.targets.length; i += 1) {
      into.addFloat(this.targets[i].integrity);
      into.addInt(this.targets[i].destroyed ? 1 : 0);
    }
  }
}

/** Grade shown in the debrief, for convenience at the call site. */
export { ImpactGrade };
